import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import {
  FiSend, FiSearch, FiMessageSquare, FiAlertTriangle, FiMic, FiTrash2,
  FiPaperclip, FiFileText, FiDownload, FiClock, FiFilm, FiImage,
  FiPause, FiPlay,
} from 'react-icons/fi';
import { BsCheck2, BsCheck2All, BsRobot } from 'react-icons/bs';

// WhatsApp-Web-style inbox for the AmalERP Cloud API number — ported from the
// Switcher support portal's inbox (same UX) onto AmalERP's schema and theme.
//
// Reads: one live listener on conversations (limited) and one on the OPEN
// conversation's newest messages. Nothing else is ever read, so it stays fast.
//
// What a bubble can carry: text, an attachment (image / voice note / video /
// document — fetched from Meta on demand), the Roman-Urdu TRANSCRIPT of a voice
// note, and the AI's reading of a screenshot — both written by the webhook, so
// nobody has to play or open anything just to know what the customer wanted.

const DAY_MS = 24 * 60 * 60 * 1000;
const MAROON = '#800020';

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    const yest = new Date(now.getTime() - DAY_MS);
    if (d.toDateString() === yest.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

const clockTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const dayLabel = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yest = new Date(now.getTime() - DAY_MS);
    if (d.toDateString() === yest.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const initials = (s) => String(s || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

/** WhatsApp only takes four kinds; anything else travels as a document. */
const kindOf = (file) => {
  const t = String(file.type || '');
  if (t.startsWith('image/') && t !== 'image/webp') return 'image';
  if (t.startsWith('video/')) return 'video';
  return 'document';
};

const Tick = ({ status }) => {
  if (status === 'failed') return <TickWrap $failed title="Failed"><FiAlertTriangle size={12} /></TickWrap>;
  if (status === 'read') return <TickWrap $read title="Read"><BsCheck2All size={14} /></TickWrap>;
  if (status === 'delivered') return <TickWrap title="Delivered"><BsCheck2All size={14} /></TickWrap>;
  if (status === 'sent') return <TickWrap title="Sent"><BsCheck2 size={14} /></TickWrap>;
  return <TickWrap title="Sending"><FiClock size={11} /></TickWrap>;
};

/**
 * A photo, voice note, video or document on a message. The bytes sit on Meta's
 * servers behind the API token, so they're fetched via the getWhatsappMedia
 * callable. Images and voice notes load themselves (that's what you want to see
 * instantly); video/documents load on click to save bandwidth. Meta keeps media
 * ~30 days; after that we say so instead of showing a broken box.
 */
function Attachment({ m, media, loadMedia }) {
  const st = media[m.id] || {};
  const auto = m.type === 'image' || m.type === 'sticker' || m.type === 'audio';

  useEffect(() => {
    if (auto && m.mediaId && !st.dataUrl && !st.loading && !st.error) loadMedia(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.id]);

  if (!m.mediaId) return null;

  if (st.error) {
    return <MediaNote $err><FiAlertTriangle size={13} /> {st.error}</MediaNote>;
  }
  if (!st.dataUrl) {
    if (auto || st.loading) {
      return (
        <MediaNote>
          {m.type === 'audio' ? <FiMic size={13} /> : m.type === 'video' ? <FiFilm size={13} /> : <FiImage size={13} />}
          Loading…
        </MediaNote>
      );
    }
    return (
      <LoadBtn type="button" onClick={() => loadMedia(m)}>
        {m.type === 'video' ? <FiFilm size={13} /> : <FiFileText size={13} />}
        Load {m.type === 'video' ? 'video' : (m.filename || 'document')}
      </LoadBtn>
    );
  }

  if (m.type === 'image' || m.type === 'sticker') {
    return (
      <MediaImg href={st.dataUrl} target="_blank" rel="noreferrer">
        <img src={st.dataUrl} alt={m.filename || 'Photo'} />
      </MediaImg>
    );
  }
  if (m.type === 'audio') {
    return (
      <MediaAudio>
        {m.voice && <FiMic size={13} />}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio src={st.dataUrl} controls preload="metadata" />
      </MediaAudio>
    );
  }
  if (m.type === 'video') {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <MediaVideo src={st.dataUrl} controls preload="metadata" />;
  }
  return (
    <MediaFile href={st.dataUrl} download={m.filename || 'file'}>
      <FiFileText size={15} />
      <span>{m.filename || 'Document'}</span>
      <FiDownload size={13} />
    </MediaFile>
  );
}

const WhatsappInbox = () => {
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [filter, setFilter] = useState('all');
  // Optimistic outbound messages shown instantly while the send runs in the
  // background: { tempId, to, text, timestamp, status:'sending'|'failed', error }.
  const [pending, setPending] = useState([]);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [media, setMedia] = useState({}); // msgId -> { loading, dataUrl, mime, error }
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const recorderRef = useRef(null);
  const recTimerRef = useRef(null);
  const recCancelRef = useRef(false);

  // Conversation list (live).
  useEffect(() => {
    const q = query(collection(db, 'whatsappInbox'), orderBy('lastMessageAt', 'desc'), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      setConversations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoadingConvs(false);
    }, () => setLoadingConvs(false));
    return () => unsub();
  }, []);

  // Global agent switch (whatsappInbox/_config → agentMode 'live' | 'off').
  const [agentMode, setAgentMode] = useState('live');
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'whatsappInbox', '_config'),
      (d) => setAgentMode(d.exists() ? (d.data().agentMode || 'live') : 'live'), () => {});
    return () => unsub();
  }, []);
  const toggleAgentGlobal = async () => {
    try {
      await setDoc(doc(db, 'whatsappInbox', '_config'),
        { agentMode: agentMode === 'off' ? 'live' : 'off' }, { merge: true });
    } catch (e) { setErr('Could not change agent mode.'); }
  };

  // Messages of the selected conversation (live). NEWEST 300, shown in order —
  // ascending+limit would pin the OLDEST 300 and new messages would never appear
  // once a chat grows past the limit.
  useEffect(() => {
    if (!selectedId) { setMessages([]); return; }
    const q = query(collection(db, 'whatsappInbox', selectedId, 'messages'), orderBy('timestamp', 'desc'), limit(300));
    const unsub = onSnapshot(q, (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse()));
    return () => unsub();
  }, [selectedId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, pending, selectedId]);

  const selected = useMemo(() => conversations.find((c) => c.id === selectedId) || null, [conversations, selectedId]);
  const windowOpen = selected?.lastInboundAt ? (Date.now() - new Date(selected.lastInboundAt).getTime()) < DAY_MS : false;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations.filter((c) => {
      if (c.id === '_config') return false;
      if (filter === 'unread' && !(c.unreadCount > 0)) return false;
      if (filter === 'human' && !c.needsHuman) return false;
      if (!q) return true;
      return (c.name || '').toLowerCase().includes(q) || (c.phone || c.id || '').includes(q);
    });
  }, [conversations, search, filter]);

  const needHumanCount = useMemo(() => conversations.filter((c) => c.needsHuman).length, [conversations]);

  // Per-chat agent pause/resume (button se roka hua sirf button se khulta hai).
  const toggleAgentChat = async () => {
    if (!selected) return;
    try {
      await updateDoc(doc(db, 'whatsappInbox', selected.id), selected.agentPaused
        ? { agentPaused: false, pausedBy: null }
        : { agentPaused: true, pausedBy: 'button' });
    } catch (e) { setErr('Could not change agent state.'); }
  };

  const clearHuman = async () => {
    if (!selected) return;
    try {
      await updateDoc(doc(db, 'whatsappInbox', selected.id), { needsHuman: false, needsHumanReason: null });
    } catch (e) { setErr('Could not clear the flag.'); }
  };

  const openConv = async (c) => {
    if (recording) cancelRecording();
    setSelectedId(c.id); setErr('');
    if (c.unreadCount > 0) { try { await updateDoc(doc(db, 'whatsappInbox', c.id), { unreadCount: 0 }); } catch (_) { /* ignore */ } }
  };

  // ── Text send (optimistic): the bubble appears the instant you press Enter
  // and the actual WhatsApp send runs in the background. On success the real
  // message arrives via onSnapshot and the temp bubble is dropped; on failure
  // the temp bubble stays with a Retry so nothing is silently lost.
  const send = () => {
    const text = draft.trim();
    if (!text || !selected) return;
    setErr('');
    const to = selected.id;
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setPending((prev) => [...prev, { tempId, to, text, timestamp: new Date().toISOString(), status: 'sending' }]);
    setDraft('');
    deliverText(tempId, to, text);
  };

  const deliverText = async (tempId, to, text) => {
    try {
      const fn = httpsCallable(functions, 'sendWhatsappReply');
      await fn({ to, text });
      setPending((prev) => prev.filter((p) => p.tempId !== tempId)); // real msg now in snapshot
    } catch (e) {
      const msg = (e && e.message) ? e.message.replace(/^.*?:\s*/, '') : 'Failed to send.';
      setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, status: 'failed', error: msg } : p)));
    }
  };

  const retryPending = (p) => {
    setPending((prev) => prev.map((x) => (x.tempId === p.tempId ? { ...x, status: 'sending', error: '' } : x)));
    deliverText(p.tempId, p.to, p.text);
  };
  const dismissPending = (tempId) => setPending((prev) => prev.filter((p) => p.tempId !== tempId));

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  // On-demand fetch of a media file from Meta, as a data URL.
  const loadMedia = async (m) => {
    const cur = media[m.id];
    if (cur && (cur.loading || cur.dataUrl)) return;
    setMedia((prev) => ({ ...prev, [m.id]: { loading: true } }));
    try {
      const fn = httpsCallable(functions, 'getWhatsappMedia');
      const res = await fn({ mediaId: m.mediaId });
      setMedia((prev) => ({ ...prev, [m.id]: { dataUrl: res?.data?.dataUrl || '', mime: res?.data?.mimeType || '' } }));
    } catch (e) {
      setMedia((prev) => ({ ...prev, [m.id]: { error: (e && e.message) ? e.message.replace(/^.*?:\s*/, '') : 'Could not load' } }));
    }
  };

  // ── File / photo send ──
  const sendFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // same file dobara chun sakein
    if (!file || !selected || fileBusy) return;
    if (file.size > 7.5 * 1024 * 1024) { setErr('File 7MB se bari hai — chhoti karke bhejein.'); return; }
    const who = selected.name || `+${selected.phone || selected.id}`;
    // WhatsApp par bheja hua wapas nahi aata — ek dafa poochh lena behtar hai.
    // eslint-disable-next-line no-alert
    if (!window.confirm(`"${file.name}" ${who} ko WhatsApp par bhej dein?\n\nBhejne ke baad wapas nahi liya ja sakta.`)) return;
    setFileBusy(true); setErr('');
    try {
      const dataUrl = await blobToDataUrl(file);
      const b64 = String(dataUrl).split(',')[1] || '';
      const fn = httpsCallable(functions, 'sendWhatsappMedia');
      const res = await fn({
        to: selected.id,
        kind: kindOf(file),
        mimeType: file.type || 'application/octet-stream',
        filename: file.name,
        caption: draft.trim(),
        dataBase64: b64,
      });
      const id = res?.data?.id;
      if (id) setMedia((prev) => ({ ...prev, [id]: { dataUrl, mime: file.type || '' } })); // instant local preview
      setDraft('');
    } catch (e2) {
      setErr((e2 && e2.message) ? e2.message.replace(/^.*?:\s*/, '') : 'File nahi ja saki.');
    }
    setFileBusy(false);
  };

  // ── Voice notes ──
  // Record ogg/opus in the browser (opus-recorder → real WhatsApp voice bubble),
  // then upload+send via the sendWhatsappVoice function. Lazy-imported so a load
  // failure never affects the rest of the inbox.
  const stopRecTimer = () => { if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; } };

  const startRecording = async () => {
    if (recording || voiceBusy || !selected) return;
    setErr('');
    try {
      const { default: Recorder } = await import('opus-recorder');
      const rec = new Recorder({
        encoderPath: (process.env.PUBLIC_URL || '') + '/opus/encoderWorker.min.js',
        numberOfChannels: 1,
        encoderApplication: 2048, // VOIP — tuned for speech
        encoderSampleRate: 16000,
        streamPages: false, // hand back the whole .ogg once, on stop
      });
      rec.ondataavailable = (typedArray) => {
        if (recCancelRef.current) { recCancelRef.current = false; return; }
        sendVoiceBlob(new Blob([typedArray], { type: 'audio/ogg' }));
      };
      recorderRef.current = rec;
      await rec.start();
      recCancelRef.current = false;
      setRecSecs(0);
      setRecording(true);
      recTimerRef.current = setInterval(() => setRecSecs((s) => {
        const n = s + 1;
        if (n >= 300) setTimeout(() => sendRecording(), 0); // safety auto-stop at 5 min
        return n;
      }), 1000);
    } catch (e) {
      console.error('voice start failed', e);
      setErr('Microphone not available or blocked. Allow mic access in your browser and try again.');
      recorderRef.current = null;
    }
  };

  const sendRecording = () => {
    const rec = recorderRef.current;
    if (!rec || !recording) return;
    recCancelRef.current = false;
    setRecording(false);
    stopRecTimer();
    setVoiceBusy(true);
    try { rec.stop(); } catch (e) { setVoiceBusy(false); }
  };

  const cancelRecording = () => {
    const rec = recorderRef.current;
    recCancelRef.current = true;
    setRecording(false);
    stopRecTimer();
    setRecSecs(0);
    try { rec && rec.stop(); } catch (_) { /* ignore */ }
    recorderRef.current = null;
  };

  const sendVoiceBlob = async (blob) => {
    try {
      if (!blob || !blob.size || !selected) { setVoiceBusy(false); return; }
      const dataUrl = await blobToDataUrl(blob);
      const b64 = String(dataUrl).split(',')[1] || '';
      const fn = httpsCallable(functions, 'sendWhatsappVoice');
      const res = await fn({ to: selected.id, audioBase64: b64, mimeType: 'audio/ogg' });
      const id = res?.data?.id;
      if (id) setMedia((prev) => ({ ...prev, [id]: { dataUrl, mime: 'audio/ogg' } })); // instant local playback
    } catch (e) {
      setErr((e && e.message) ? e.message.replace(/^.*?:\s*/, '') : 'Failed to send voice note.');
    } finally {
      setVoiceBusy(false);
      setRecSecs(0);
      recorderRef.current = null;
    }
  };

  // Stop any recording on unmount.
  useEffect(() => () => { stopRecTimer(); try { recorderRef.current && recorderRef.current.stop(); } catch (_) { /* ignore */ } }, []);

  // Day dividers between message groups.
  const grouped = useMemo(() => {
    const out = []; let last = '';
    messages.forEach((m) => {
      const d = dayLabel(m.timestamp);
      if (d && d !== last) { out.push({ divider: d, id: 'd-' + m.id }); last = d; }
      out.push(m);
    });
    return out;
  }, [messages]);

  const shownPending = pending.filter((p) => p.to === selectedId
    // Hide a 'sending' temp once its real message has landed via the live
    // listener (avoids a brief duplicate); keep 'failed' visible.
    && !(p.status === 'sending' && messages.some((m) => m.direction === 'out' && (m.text || '') === p.text && m.timestamp >= p.timestamp)));

  const isPlaceholder = (t) => t && /^\[[a-z ]+\]$/i.test(t);

  return (
    <Wrap>
      {/* ── Left: conversations ── */}
      <ListPane $hasActive={!!selectedId}>
        <ListHead>
          <SearchRow>
            <FiSearch size={15} />
            <SearchInput placeholder="Search name or number…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </SearchRow>
          <Filters>
            {[['all', 'All'], ['unread', 'Unread'], ['human', `Needs human${needHumanCount ? ` (${needHumanCount})` : ''}`]].map(([k, l]) => (
              <FilterBtn key={k} type="button" $on={filter === k} onClick={() => setFilter(k)}>{l}</FilterBtn>
            ))}
            <AgentToggle type="button" $on={agentMode !== 'off'} onClick={toggleAgentGlobal}
              title={agentMode !== 'off' ? 'AI agent is answering — click to switch off' : 'AI agent is OFF — click to switch on'}>
              <BsRobot size={13} /> {agentMode !== 'off' ? 'Agent ON' : 'Agent OFF'}
            </AgentToggle>
          </Filters>
        </ListHead>
        <ConvScroll>
          {loadingConvs && <Empty>Loading…</Empty>}
          {!loadingConvs && filtered.length === 0 && (
            <Empty>No conversations yet. They appear here when a customer messages your WhatsApp number.</Empty>
          )}
          {filtered.map((c) => (
            <ConvRow key={c.id} type="button" $active={c.id === selectedId} onClick={() => openConv(c)}>
              <Avatar $warn={!!c.needsHuman}>{initials(c.name || c.phone || c.id)}</Avatar>
              <ConvMid>
                <ConvTop>
                  <ConvName>{c.name || `+${c.phone || c.id}`}</ConvName>
                  <ConvTime>{fmtTime(c.lastMessageAt)}</ConvTime>
                </ConvTop>
                <ConvBottom>
                  <ConvPreview>
                    {c.lastDirection === 'out' && <YouPrefix>You: </YouPrefix>}
                    {c.lastMessage || '—'}
                  </ConvPreview>
                  {c.needsHuman && <HumanChip>human</HumanChip>}
                  {c.unreadCount > 0 && <Unread>{c.unreadCount}</Unread>}
                </ConvBottom>
              </ConvMid>
            </ConvRow>
          ))}
        </ConvScroll>
      </ListPane>

      {/* ── Right: thread ── */}
      <ChatPane $open={!!selectedId}>
        {!selected ? (
          <NoChat>
            <FiMessageSquare size={40} />
            <p>Pick a conversation on the left.</p>
            <span>Replies, voice notes, photos and files — everything your customers send to the AmalERP number shows up here, live.</span>
          </NoChat>
        ) : (
          <>
            <ChatHeader>
              <BackBtn type="button" onClick={() => setSelectedId(null)}>‹</BackBtn>
              <Avatar $warn={!!selected.needsHuman}>{initials(selected.name || selected.phone || selected.id)}</Avatar>
              <ChatWho>
                <ChatName>{selected.name || `+${selected.phone || selected.id}`}</ChatName>
                <ChatSub>+{selected.phone || selected.id}</ChatSub>
              </ChatWho>
              <AgentState $on={agentMode !== 'off' && !selected.agentPaused}>
                <BsRobot size={12} />
                {agentMode === 'off' ? 'Agent off' : selected.agentPaused ? 'Agent quiet' : 'Agent on'}
              </AgentState>
              <HeadBtn type="button" onClick={toggleAgentChat}
                title={selected.agentPaused ? 'Resume agent on this chat' : 'Pause agent on this chat'}>
                {selected.agentPaused ? <FiPlay size={14} /> : <FiPause size={14} />}
              </HeadBtn>
              <WindowState $open={windowOpen}>
                {windowOpen ? 'Window open' : '24h window closed'}
              </WindowState>
            </ChatHeader>

            {selected.needsHuman && (
              <HumanBanner>
                <FiAlertTriangle size={14} />
                <span>{selected.needsHumanReason || 'Is chat ko insaan ki zaroorat hai.'}</span>
                <button type="button" onClick={clearHuman}>Done</button>
              </HumanBanner>
            )}

            {selected.agentPaused && selected.pausedBy === 'reply' && (
              <QuietNote>
                Aap ne haath se reply kiya tha, is liye agent is chat par chup hai — customer ke agle message par khud dobara sambhal lega.
              </QuietNote>
            )}

            <Thread>
              {grouped.map((m) => (m.divider ? (
                <Divider key={m.id}><span>{m.divider}</span></Divider>
              ) : (
                <Row key={m.id} $out={m.direction === 'out'}>
                  <Bubble $out={m.direction === 'out'} $failed={m.status === 'failed'}>
                    {m.mediaId && <Attachment m={m} media={media} loadMedia={loadMedia} />}
                    {/* A voice note reads back as text so nobody has to play it
                        just to find out what the customer wanted. */}
                    {m.transcript && <Transcript title="Voice note ka matn">&ldquo;{m.transcript}&rdquo;</Transcript>}
                    {!m.transcript && m.transcriptWhy && (
                      <Transcript $warn title="Awaz kyun nahi parhi ja saki">Awaz nahi parhi ja saki: {m.transcriptWhy}</Transcript>
                    )}
                    {/* What the AI saw on a screenshot — the issue is readable
                        without opening the image. */}
                    {m.imageRead && <Transcript title="Tasveer me kya hai">{m.imageRead}</Transcript>}
                    {m.text && !(m.mediaId && isPlaceholder(m.text)) && !m.transcript && <BubbleText>{m.text}</BubbleText>}
                    {m.text && m.transcript && !isPlaceholder(m.text) && <BubbleText>{m.text}</BubbleText>}
                    <BubbleMeta>
                      {m.direction === 'out' && m.byAgent && <AgentTag title="AI agent ne bheja"><BsRobot size={11} /></AgentTag>}
                      {m.templateName && <TplTag>template</TplTag>}
                      <span>{clockTime(m.timestamp)}</span>
                      {m.direction === 'out' && <Tick status={m.status} />}
                    </BubbleMeta>
                    {m.deliveryError && <BubbleErr>{m.deliveryError}</BubbleErr>}
                  </Bubble>
                </Row>
              )))}
              {shownPending.map((p) => (
                <Row key={p.tempId} $out>
                  <Bubble $out $failed={p.status === 'failed'}>
                    <BubbleText>{p.text}</BubbleText>
                    <BubbleMeta>
                      <span>{clockTime(p.timestamp)}</span>
                      {p.status === 'failed' ? <Tick status="failed" /> : <Tick status="" />}
                    </BubbleMeta>
                    {p.status === 'failed' && (
                      <BubbleErr>
                        {p.error || 'Failed.'}{' '}
                        <RetryLink onClick={() => retryPending(p)}>Retry</RetryLink> ·{' '}
                        <RetryLink onClick={() => dismissPending(p.tempId)}>Dismiss</RetryLink>
                      </BubbleErr>
                    )}
                  </Bubble>
                </Row>
              ))}
              {messages.length === 0 && shownPending.length === 0 && <Empty style={{ margin: 'auto' }}>No messages.</Empty>}
              <div ref={endRef} />
            </Thread>

            {err && <SendErr>{err}</SendErr>}

            <Composer>
              <input
                ref={fileRef}
                type="file"
                hidden
                onChange={sendFile}
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
              />
              <AttachBtn
                type="button"
                title={windowOpen ? 'Attach a photo or file' : '24-hour window is closed'}
                onClick={() => fileRef.current?.click()}
                disabled={!windowOpen || fileBusy || recording}
              >
                {fileBusy ? <MiniSpin $dark /> : <FiPaperclip size={17} />}
              </AttachBtn>

              {recording ? (
                <RecBar>
                  <RecDot />
                  <RecTime>{fmtDur(recSecs)}</RecTime>
                  <RecHint>Recording…</RecHint>
                  <IconRound title="Cancel" onClick={cancelRecording}><FiTrash2 size={16} /></IconRound>
                  <SendBtn onClick={sendRecording} title="Send voice note"><FiSend size={16} /></SendBtn>
                </RecBar>
              ) : (
                <>
                  <ComposerInput
                    placeholder={windowOpen ? 'Type a reply…  (Enter to send)' : '24-hour window closed — customer must message first'}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    rows={1}
                    disabled={!windowOpen || voiceBusy}
                  />
                  {draft.trim() ? (
                    <SendBtn onClick={send} disabled={!windowOpen}><FiSend size={16} /></SendBtn>
                  ) : (
                    <SendBtn onClick={startRecording} disabled={!windowOpen || voiceBusy} title="Record voice note">
                      {voiceBusy ? <MiniSpin /> : <FiMic size={18} />}
                    </SendBtn>
                  )}
                </>
              )}
            </Composer>
            {!windowOpen && (
              <WindowNote>
                WhatsApp rule: 24 hours after the customer&apos;s last message, only an approved template can be sent
                (use the WhatsApp broadcast for that). Free replies open up again as soon as they message you.
              </WindowNote>
            )}
          </>
        )}
      </ChatPane>
    </Wrap>
  );
};

// ── styles — WhatsApp-Web layout, AmalERP (burgundy) theme ──

const Wrap = styled.div`
  display: flex; height: 76vh; min-height: 540px;
  border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; background: #fff;
`;

const ListPane = styled.div`
  width: 340px; min-width: 300px; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; background: #fff;
  @media (max-width: 820px) {
    width: 100%; min-width: 0; flex: 1 1 auto; border-right: none;
    display: ${(p) => (p.$hasActive ? 'none' : 'flex')};
  }
`;
const ListHead = styled.div` padding: 12px; border-bottom: 1px solid #f1f1f1; `;
const SearchRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 8px 10px;
  background: #f4f4f5; border-radius: 8px; color: #6b7280;
`;
const SearchInput = styled.input`
  flex: 1; border: none; background: transparent; outline: none; font-size: 13.5px; color: #111827;
`;
const Filters = styled.div` display: flex; gap: 6px; margin-top: 10px; `;
const FilterBtn = styled.button`
  border: 1px solid ${(p) => (p.$on ? MAROON : '#e5e7eb')};
  background: ${(p) => (p.$on ? MAROON : '#fff')};
  color: ${(p) => (p.$on ? '#fff' : '#6b7280')};
  border-radius: 999px; padding: 4px 12px; font-size: 12px; font-weight: 600; cursor: pointer;
`;

const ConvScroll = styled.div` flex: 1; overflow-y: auto; `;
const ConvRow = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
  padding: 10px 12px; border: 0; border-bottom: 1px solid #f5f5f5; cursor: pointer;
  background: ${(p) => (p.$active ? '#fdf2f4' : '#fff')};
  &:hover { background: ${(p) => (p.$active ? '#fdf2f4' : '#fafafa')}; }
`;
const Avatar = styled.div`
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  background: ${(p) => (p.$warn ? '#d97706' : MAROON)}; color: #fff; font-weight: 700; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
`;
const HumanChip = styled.span`
  font-size: 10px; border-radius: 999px; padding: 1px 7px; font-weight: 700; white-space: nowrap;
  background: #fef3c7; color: #92400e; flex-shrink: 0;
`;
const AgentToggle = styled.button`
  display: inline-flex; align-items: center; gap: 5px; margin-left: auto;
  border: 1px solid ${(p) => (p.$on ? '#a7f3d0' : '#e5e7eb')};
  background: ${(p) => (p.$on ? '#ecfdf5' : '#f4f4f5')};
  color: ${(p) => (p.$on ? '#047857' : '#6b7280')};
  border-radius: 999px; padding: 4px 10px; font-size: 11.5px; font-weight: 700; cursor: pointer;
`;
const AgentState = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 600; border-radius: 999px; padding: 3px 9px; white-space: nowrap;
  background: ${(p) => (p.$on ? '#ecfdf5' : '#f4f4f5')};
  color: ${(p) => (p.$on ? '#047857' : '#6b7280')};
`;
const HeadBtn = styled.button`
  border: 1px solid #e5e7eb; background: #fff; border-radius: 8px;
  padding: 6px 8px; cursor: pointer; color: #374151; display: flex; align-items: center;
  &:hover { background: #f9fafb; }
`;
const HumanBanner = styled.div`
  display: flex; align-items: center; gap: 8px;
  background: #fef3c7; color: #92400e; padding: 8px 14px; font-size: 12.5px;
  span { flex: 1; }
  button {
    border: 1px solid #d97706; background: #fff; color: #92400e;
    border-radius: 6px; padding: 3px 12px; font-size: 12px; font-weight: 600; cursor: pointer;
  }
`;
const QuietNote = styled.div`
  background: #f4f4f5; color: #6b7280; padding: 7px 14px; font-size: 12px;
`;
const AgentTag = styled.span` display: inline-flex; align-items: center; color: #9ca3af; `;
const TplTag = styled.span`
  background: #e0f2fe; color: #0369a1; border-radius: 4px; padding: 0 4px; font-size: 10px;
`;
const ConvMid = styled.div` flex: 1; min-width: 0; `;
const ConvTop = styled.div` display: flex; justify-content: space-between; gap: 8px; align-items: baseline; `;
const ConvName = styled.div`
  font-size: 14px; font-weight: 600; color: #111827;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ConvTime = styled.div` font-size: 11px; color: #9ca3af; white-space: nowrap; `;
const ConvBottom = styled.div` display: flex; align-items: center; gap: 6px; margin-top: 2px; `;
const ConvPreview = styled.div`
  flex: 1; font-size: 12.5px; color: #6b7280;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const YouPrefix = styled.span` color: #9ca3af; `;
const Unread = styled.div`
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: ${MAROON}; color: #fff;
  font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;

const ChatPane = styled.div`
  flex: 1; display: flex; flex-direction: column; background: #f3efe9; min-width: 0;
  @media (max-width: 820px) { display: ${(p) => (p.$open ? 'flex' : 'none')}; }
`;
const NoChat = styled.div`
  margin: auto; text-align: center; color: #6b7280; padding: 24px; max-width: 360px;
  p { margin: 12px 0 4px; font-size: 15px; color: #374151; }
  span { font-size: 12.5px; color: #9ca3af; line-height: 1.5; }
  svg { color: #d1d5db; }
`;
const ChatHeader = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 14px;
  background: #fff; border-bottom: 1px solid #e5e7eb;
`;
const BackBtn = styled.button`
  display: none; border: 0; background: transparent; font-size: 26px; line-height: 1; color: #374151; cursor: pointer;
  @media (max-width: 820px) { display: block; }
`;
const ChatWho = styled.div` flex: 1; min-width: 0; `;
const ChatName = styled.div` font-size: 14.5px; font-weight: 600; color: #111827; `;
const ChatSub = styled.div` font-size: 11.5px; color: #6b7280; `;
const WindowState = styled.span`
  font-size: 11px; font-weight: 600; border-radius: 999px; padding: 3px 10px; white-space: nowrap;
  background: ${(p) => (p.$open ? '#ecfdf5' : '#f4f4f5')};
  color: ${(p) => (p.$open ? '#047857' : '#6b7280')};
`;

const Thread = styled.div` flex: 1; overflow-y: auto; padding: 16px 18px; display: flex; flex-direction: column; `;
const Divider = styled.div`
  text-align: center; margin: 12px 0;
  span {
    background: #fff; color: #6b7280; font-size: 11px;
    border-radius: 999px; padding: 3px 12px; box-shadow: 0 1px 1px rgba(0,0,0,.06);
  }
`;
const Row = styled.div` display: flex; margin-bottom: 7px; justify-content: ${(p) => (p.$out ? 'flex-end' : 'flex-start')}; `;
const Bubble = styled.div`
  max-width: min(78%, 560px);
  background: ${(p) => (p.$failed ? '#fee2e2' : p.$out ? '#fdf2f4' : '#fff')};
  border-radius: 10px; padding: 6px 9px 4px;
  box-shadow: 0 1px 1px rgba(0,0,0,.08);
`;
const BubbleText = styled.p`
  margin: 0; font-size: 13.5px; color: #111827; white-space: pre-wrap; word-break: break-word;
`;
const BubbleMeta = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 5px;
  margin-top: 2px; font-size: 10.5px; color: #9ca3af;
`;
const BubbleErr = styled.div` font-size: 11px; color: #b91c1c; margin-top: 3px; `;
const TickWrap = styled.span`
  display: inline-flex; align-items: center;
  color: ${(p) => (p.$failed ? '#b91c1c' : p.$read ? '#0ea5e9' : '#9ca3af')};
`;
const Transcript = styled.p`
  font-style: italic; font-size: 12.5px; line-height: 1.5;
  color: ${(p) => (p.$warn ? '#92400e' : '#4b5563')};
  margin: 6px 0 2px; padding-left: 8px; border-left: 2px solid ${(p) => (p.$warn ? '#fcd34d' : '#d6b2bc')};
`;
const RetryLink = styled.span` color: ${MAROON}; font-weight: 700; cursor: pointer; &:hover { text-decoration: underline; } `;

const MediaNote = styled.div`
  display: flex; align-items: center; gap: 6px; font-size: 12px;
  color: ${(p) => (p.$err ? '#b91c1c' : '#6b7280')}; padding: 5px 0;
`;
const LoadBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; margin: 3px 0;
  font-size: 12px; padding: 5px 12px; border-radius: 14px; cursor: pointer;
  border: 1px solid ${MAROON}; background: #fff; color: ${MAROON}; font-weight: 600;
`;
const MediaImg = styled.a`
  display: block; margin: 2px 0 4px; border-radius: 8px; overflow: hidden; line-height: 0; max-width: 260px;
  img { display: block; width: 100%; max-height: 320px; object-fit: cover; cursor: zoom-in; }
`;
const MediaVideo = styled.video` max-width: 260px; width: 100%; border-radius: 8px; background: #0f172a; margin: 2px 0 4px; `;
const MediaAudio = styled.div`
  display: flex; align-items: center; gap: 6px; margin: 2px 0 2px; color: #6b7280;
  audio { width: 230px; height: 34px; }
`;
const MediaFile = styled.a`
  display: flex; align-items: center; gap: 8px; padding: 9px 11px; margin: 2px 0 4px;
  background: rgba(0,0,0,.05); border-radius: 8px; max-width: 260px;
  color: #111827; text-decoration: none; font-size: 13px;
  span { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &:hover { background: rgba(0,0,0,.09); }
`;

const SendErr = styled.div`
  margin: 0 12px 6px; padding: 8px 10px; border-radius: 8px;
  background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; font-size: 12px;
`;
const Composer = styled.div`
  display: flex; align-items: flex-end; gap: 8px; padding: 10px 12px;
  background: #f6f7f8; border-top: 1px solid #e5e7eb;
`;
const AttachBtn = styled.button`
  width: 40px; height: 40px; border-radius: 50%; border: none; background: transparent; color: #6b7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  &:hover:not(:disabled) { background: #ececec; color: #374151; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const ComposerInput = styled.textarea`
  flex: 1; resize: none; max-height: 120px; border: 1px solid #d1d5db; border-radius: 20px; padding: 9px 14px;
  font-size: 13.5px; outline: none; font-family: inherit; color: #111827; background: #fff;
  &:focus { border-color: ${MAROON}; }
  &:disabled { background: #f4f4f5; color: #9ca3af; }
`;
const SendBtn = styled.button`
  width: 42px; height: 42px; border-radius: 50%; border: none; background: ${MAROON}; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const WindowNote = styled.p`
  margin: 0; padding: 0 14px 10px; font-size: 11.5px; color: #6b7280; background: #f6f7f8; line-height: 1.5;
`;
const Empty = styled.div` padding: 24px 16px; text-align: center; color: #9ca3af; font-size: 13px; line-height: 1.5; `;
const RecBar = styled.div` flex: 1; display: flex; align-items: center; gap: 10px; `;
const RecDot = styled.span`
  width: 10px; height: 10px; border-radius: 50%; background: #ef4444; flex-shrink: 0;
  animation: recpulse 1s infinite;
  @keyframes recpulse { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
`;
const RecTime = styled.span` font-size: 13px; font-weight: 700; color: #111827; min-width: 42px; font-variant-numeric: tabular-nums; `;
const RecHint = styled.span` flex: 1; font-size: 12.5px; color: #6b7280; `;
const IconRound = styled.button`
  width: 42px; height: 42px; border-radius: 50%; border: none; background: #e5e7eb; color: #374151; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  &:hover { background: #dbe0e4; }
`;
const MiniSpin = styled.span`
  width: 16px; height: 16px; border: 2px solid ${(p) => (p.$dark ? 'rgba(0,0,0,.2)' : 'rgba(255,255,255,.5)')};
  border-top-color: ${(p) => (p.$dark ? '#6b7280' : '#fff')}; border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export default WhatsappInbox;
